/* eslint-disable react-refresh/only-export-components -- helper function co-located with component for cohesion */
import { useMemo, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { countriesQuery, type Country } from '@/data';
import { ColorBar } from './ColorBar';
import { useQuizStore } from '../state';
import { DEFAULT_WEIGHTS, topReasons, type CountryMatchResult, type TFn } from '..';
import { uiLang } from '@/i18n/lang';
import { submitNotify } from '@/lib/notify';

const EMAIL_CAPTURED_KEY = 'rm:email-captured:v1';

export function hasEmailBeenCaptured(): boolean {
  try {
    return localStorage.getItem(EMAIL_CAPTURED_KEY) === '1';
  } catch {
    return false;
  }
}

interface EmailGateProps {
  onContinue: () => void;
}

export function EmailGate({ onContinue }: EmailGateProps) {
  const { t, i18n } = useTranslation();
  const lang = uiLang(i18n.language);
  const result = useQuizStore((s) => s.result);
  const { data: countriesFile } = useQuery(countriesQuery());

  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const byCode = useMemo<Map<string, Country>>(
    () => new Map((countriesFile?.countries ?? []).map((c) => [c.code, c])),
    [countriesFile],
  );

  const top: CountryMatchResult | undefined = result?.topMatches[0];
  const topCountry = top ? byCode.get(top.countryCode) : undefined;
  const rest = result?.topMatches.slice(1, 5) ?? [];
  const restCount = (result?.topMatches.length ?? 0) - 1;
  const percent = top ? Math.round(top.score * 100) : 0;
  const topName = topCountry
    ? lang === 'ru'
      ? topCountry.name_ru
      : topCountry.name_en
    : (top?.countryCode ?? '');
  const topFlag = topCountry?.flag_emoji ?? '';

  const reasons = useMemo(
    () => (top ? topReasons(top, DEFAULT_WEIGHTS, t as TFn).slice(0, 3) : []),
    [top, t],
  );

  // The gate promises a one-time link and then unlocks the ranking. Both the
  // promise and the "we already have this address" flag are only true when the
  // API accepted the address, so we wait for the answer (capped by the timeout
  // inside submitNotify) instead of firing and forgetting. A failure never
  // strands the visitor: the skip button below still opens the full ranking.
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.includes('@') || submitting) return;
    setSubmitting(true);
    setFailed(false);
    const ok = await submitNotify({
      email,
      countryCode: top?.countryCode ?? '',
      source: 'quiz_save',
      context: { topCountry: top?.countryCode ?? null },
    });
    setSubmitting(false);
    if (!ok) {
      // Do not set EMAIL_CAPTURED_KEY — a false flag would suppress this gate
      // on every later visit and lose the address for good, silently.
      setFailed(true);
      return;
    }
    try {
      localStorage.setItem(EMAIL_CAPTURED_KEY, '1');
    } catch {
      // intentional — error silently ignored
    }
    onContinue();
  };

  if (!top) {
    onContinue();
    return null;
  }

  return (
    <div className="mx-auto flex w-full max-w-lg flex-col gap-8">
      {/* Label */}
      <p className="font-mono text-xs font-medium uppercase tracking-widest text-terra-600">
        {t('quiz.emailGate.label')}
      </p>

      {/* Top match — fully visible */}
      <div className="rounded-xl border border-ink-100 bg-paper-2 px-6 py-6 shadow-sm">
        <div className="flex items-baseline justify-between gap-3">
          <span className="font-mono text-[11px] uppercase tracking-wider text-ink-500">
            #1
          </span>
          <span className="font-display text-3xl font-medium tabular-nums text-terra-600">
            {percent}%
          </span>
        </div>
        <div className="mt-4 flex items-baseline gap-3">
          <span className="text-3xl leading-none" aria-hidden="true">
            {topFlag}
          </span>
          <h2 className="font-display text-2xl font-medium italic tracking-tight text-ink-900">
            {topName}
          </h2>
        </div>
        {top && (
          <div className="mt-5">
            <ColorBar components={top.components} weights={DEFAULT_WEIGHTS} t={t as TFn} />
          </div>
        )}
        {reasons.length > 0 && (
          <ul className="mt-4 space-y-1.5">
            {reasons.map((r, i) => (
              <li key={i} className="flex items-start gap-2 text-sm leading-snug text-ink-700">
                <span
                  aria-hidden="true"
                  className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-moss-600"
                />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Blurred rest */}
      {rest.length > 0 && (
        <div className="relative">
          <div className="flex flex-col gap-3 blur-sm select-none" aria-hidden="true">
            {rest.map((m) => {
              const c = byCode.get(m.countryCode);
              const name = c ? (lang === 'ru' ? c.name_ru : c.name_en) : m.countryCode;
              return (
                <div
                  key={m.countryCode}
                  className="flex items-center justify-between rounded-lg border border-ink-100 bg-paper-2 px-5 py-4"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl leading-none">{c?.flag_emoji ?? ''}</span>
                    <span className="font-display text-lg font-medium italic text-ink-900">
                      {name}
                    </span>
                  </div>
                  <span className="font-display text-xl font-medium tabular-nums text-terra-600">
                    {Math.round(m.score * 100)}%
                  </span>
                </div>
              );
            })}
          </div>
          <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-paper to-transparent" />
          <p className="mt-2 text-center font-mono text-xs tracking-wider text-ink-400">
            {t('quiz.emailGate.restLabel', { count: restCount })}
          </p>
        </div>
      )}

      {/* Email form */}
      <div className="rounded-xl border border-ink-100 bg-paper-2 px-6 py-6">
        <h3 className="font-display text-xl font-medium tracking-tight text-ink-900">
          {t('quiz.emailGate.formTitle')}
        </h3>
        <p className="mt-1.5 text-sm leading-relaxed text-ink-500">
          {t('quiz.emailGate.formSub')}
        </p>
        <form onSubmit={handleSubmit} className="mt-5 flex flex-col gap-3 sm:flex-row">
          <input
            ref={inputRef}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('quiz.emailGate.placeholder')}
            required
            autoComplete="email"
            className="min-w-0 flex-1 rounded-md border border-ink-200 bg-paper px-4 py-2.5 font-mono text-sm text-ink-900 placeholder-ink-400 focus:outline-none focus:ring-2 focus:ring-terra-600/30 focus:ring-offset-1 focus:ring-offset-paper-2"
          />
          <button
            type="submit"
            disabled={submitting}
            className="shrink-0 rounded-md bg-terra-600 px-5 py-2.5 font-sans text-sm font-medium text-paper transition-colors hover:bg-terra-700 disabled:opacity-50"
          >
            {submitting ? t('quiz.emailGate.sending') : t('quiz.emailGate.cta')}
          </button>
        </form>
        {failed && (
          <p
            className="mt-3 font-mono text-[11px] text-rust-600"
            role="status"
            data-testid="email-gate-error"
          >
            {t('quiz.emailGate.failed')}
          </p>
        )}
        <p className="mt-3 font-mono text-[11px] text-ink-400">
          {t('quiz.emailGate.privacy')}
        </p>
      </div>

      {/* Skip */}
      <button
        type="button"
        onClick={onContinue}
        className="self-center font-mono text-xs text-ink-400 hover:text-ink-700"
      >
        {t('quiz.emailGate.skip')}
      </button>
    </div>
  );
}
